import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  Box,
  Icon,
  Step,
  Button,
  Select,
  Stepper,
  MenuItem,
  StepLabel,
  Container,
  IconButton,
  FormControl,
} from "@mui/material";
import UserInfo from "./UserInfo";
import CompanyInfo from "./CompanyInfo";
import DocumentInfo from "./DocumentInfo";
import API from "store/services/base.service";
import useUserStore from "store/zustand/user";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSvgByLanguage } from "components/Flag";
import { createAlert } from "store/utils/alertUtils";
import useCountriesStore from "store/zustand/countries";
import useLocalizationStore from "store/zustand/localization";

const SignUp = ({ isSupplier }) => {
  const [step, setStep] = useState(0);
  const methods = useForm();
  const { fetchCountries } = useCountriesStore();
  const navigate = useNavigate();
  const { setLanguage, language } = useLocalizationStore();
  const [submitting, setSubmitting] = useState(false); // Track the loading state

  const [selectedLanguage, setSelectedLanguage] = useState(language || "en");
  const { t, i18n } = useTranslation();

  const handleLanguageChange = async (lang) => {
    setSelectedLanguage(lang);
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };
  const { setEmail } = useUserStore();

  const handleNext = async () => {
    const isStepValid = await methods.trigger(); // Trigger validation for all fields in the form

    if (isStepValid) {
      if (step === 0) {
        setStep((prevStep) => Math.min(prevStep + 1, 1)); // Proceed to the next step if validation is successful
      }
      if (step === 1) {
        setStep((prevStep) => Math.min(prevStep + 1, 2)); // Proceed to the next step if validation is successful
      }
      if (step === 2) {
        // setStep((prevStep) => Math.min(prevStep + 1, 3)); // Proceed to the next step if validation is successful
        await onSubmit(methods.getValues());
      }
    }
  };

  const handleBack = () => {
    setStep((prevStep) => Math.max(prevStep - 1, 0)); // Ensure we don't go below Step 1
  };

  const onSubmit = async (data) => {
    const formData = new FormData();
    const documents = [];
    // Check and append documents if available
    if (data?.logo_image) {
      documents.push({
        file_type: "logo_image",
        file_name: data.logo_image.name,
      });
      formData.append("files", data.logo_image); // Append the logo image file
    }

    if (data?.banner_image) {
      documents.push({
        file_type: "banner_image",
        file_name: data.banner_image.name,
      });
      formData.append("files", data.banner_image); // Append the banner image file
    }

    if (data?.company_registration) {
      documents.push({
        file_type: "company_registration",
        file_name: data.company_registration.name,
        expiry: data.cr_expiry,
        number: data.cr_number,
      });
      formData.append("files", data.company_registration); // Append the registration document file
    }

    if (data?.vat_image) {
      documents.push({
        file_type: "vat_image",
        file_name: data.vat_image.name,
        expiry: data.vat_expiry,
        number: data.vat_number,
      });
      formData.append("files", data.vat_image); // Append the VAT document file
    }

    if (data?.owner_id) {
      documents.push({
        file_type: "owner_id",
        file_name: data.owner_id.name,
      });
      formData.append("files", data.owner_id); // Append the Owner ID document file
    }

    if (data?.contract_image) {
      documents.push({
        file_type: "contract_image",
        file_name: data.contract_image.name,
      });
      formData.append("files", data.contract_image); // Append the contract document file
    }

    const companyData = {
      company_name: data.company_name,
      company_legal_name_arabic: data.company_legal_name_arabic,
      company_legal_name: data.company_legal_name,
      moi_number: data.moi_number,
      country: data.country,
      address: data.address,
      city: data.city,
      nationalAddress: data.nationalAddress,
      company_owner_first_name: data.company_owner_first_name,
      company_owner_last_name: data.company_owner_last_name,
      company_owner_email: data.company_owner_email,
      company_owner_mobile: data.company_owner_mobile,
      documents: documents,
    };

    const userData = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      password: data.password,
      mobile_number: data.mobile_number,
      is_supplier: isSupplier,
    };
    // Convert company and user data to JSON and append to FormData
    formData.append("company", JSON.stringify(companyData));
    formData.append("user", JSON.stringify(userData));
    // formData.append("plan", JSON.stringify(plan));

    // Send the data using Axios
    try {
      setSubmitting(true);
      const response = await API.post(
        isSupplier ? "companies/signup/supplier" : "companies/signup/fleet",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response?.data?.isSuccess) {
        setEmail(data.email);
        navigate("/verify-code");
        createAlert({
          type: "success",
          message: "",
          title: "",
          status: true,
          notification: {
            title: response.data.messge || response?.data?.message,
            body: response.data.messge || response.data.message,
            titleArabic: response.data.messge || response.data.message,
            bodyArabic: response.data.messge || response.data.message,
          },
        });
      } else {
        createAlert({
          type: "error",
          message: "",
          title: "",
          status: true,
          notification: {
            title: response.data.messge || response.data.message,
            body: response.data.messge || response.data.message,
            titleArabic: response.data.messge || response.data.message,
            bodyArabic: response.data.messge || response.data.message,
          },
        });
      }
    } catch (error) {
      createAlert({
        type: "error",
        message: "",
        title: "",
        status: true,
        notification: {
          title: error.response.data.messge || error.response.data.message,
          body: error.response.data.messge || error.response.data.message,
          titleArabic:
            error.response.data.messge || error.response.data.message,
          bodyArabic: error.response.data.messge || error.response.data.message,
        },
      });
      console.error("Error submitting the form", error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  return (
    <FormProvider {...methods}>
      <Box
        component="nav"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 4,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
        }}
      >
        <Box
          component="img"
          src="/icon/mausool.png"
          alt="Mausool logo"
          onClick={() => navigate("/")}
          sx={{ height: 48, cursor: "pointer" }}
        />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton size={"medium"} aria-label="flag">
            {/* <SearchIcon /> */}
            <Icon sx={{ width: "1.5rem", height: "1.5rem" }} aria-label="flag">
              {getSvgByLanguage(selectedLanguage)}
            </Icon>
          </IconButton>

          <FormControl
            size="small"
            name="language"
            aria-label="language"
            sx={{
              border: "none", // No border for the FormControl
              outline: "none",
            }}
          >
            <Select
              value={selectedLanguage}
              aria-labelledby="language-select" // Correctly associates aria-labelledby
              onChange={(event) => handleLanguageChange(event.target.value)}
              MenuProps={{
                disableScrollLock: true,
              }}
              sx={{
                padding: 0,
                minWidth: 0,
                border: "none", // No border for the FormControl
                outline: "none", // Remove outline
                "& .MuiSelect-select": {
                  minWidth: "0 !important", // Make sure select width is minimal
                  border: "none", // Remove border from the select input
                },
                "& .MuiOutlinedInput-root": {
                  border: "none", // Remove border from the root input
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none", // Remove focus border
                  },
                },
              }}
            >
              <MenuItem value="en">{t("header.language.short.en")}</MenuItem>
              <MenuItem value="ar">{t("header.language.short.ar")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Container sx={{ maxWidth: "lg", margin: "auto", my: 2 }}>
        <Stepper
          activeStep={step}
          alternativeLabel
          sx={{
            flexDirection: language === "ar" ? "row-reverse" : "row", // Adjust direction for Arabic
          }}
        >
          <Step>
            <StepLabel>{t("auth.signup.steps.companyInfo")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("auth.signup.steps.documents")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("auth.signup.steps.adminInfo")}</StepLabel>
          </Step>
          {/* <Step>
            <StepLabel>{t("auth.signup.steps.plan")}</StepLabel>
          </Step> */}
        </Stepper>
        {step === 0 && <CompanyInfo handleNext={handleNext} />}
        {step === 1 && <DocumentInfo handleNext={handleNext} />}
        {step === 2 && <UserInfo handleBack={handleBack} />}
        <Box sx={{ display: "flex", justifyContent: "end", mt: 2, gap: 2 }}>
          <Button onClick={handleBack} disabled={step === 0} variant="outlined">
            {t("auth.signup.actions.back")}
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            loading={submitting}
            onClick={handleNext}
          >
            {step === 2
              ? t("auth.signup.actions.submit")
              : t("auth.signup.actions.next")}
          </Button>
        </Box>
      </Container>
    </FormProvider>
  );
};

export default SignUp;
